import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Wrench, CheckCircle, Info, Gavel, ChevronLeft, ChevronRight, X, Euro, UserPlus } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicQRPage() {
  const { scooterId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [client, setClient] = useState(null);
  const [price, setPrice] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "", name: "", phone: "", mode: "login" });

  useEffect(() => {
    const saved = localStorage.getItem("auction_client");
    if (saved) setClient(JSON.parse(saved));
    fetchData();
  }, [scooterId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/api/public/qr/${scooterId}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      if (loginForm.mode === "login") {
        const res = await axios.post(`${API}/api/auction-clients/login`, { email: loginForm.email, password: loginForm.password });
        localStorage.setItem("auction_token", res.data.token);
        localStorage.setItem("auction_client", JSON.stringify(res.data.client));
        setClient(res.data.client);
        setShowLogin(false);
        toast.success("Conectat!");
      } else {
        if (!loginForm.name || !loginForm.phone || !loginForm.email || !loginForm.password) { toast.error("Completati toate campurile"); return; }
        const res = await axios.post(`${API}/api/auction-clients/register`, loginForm);
        const pwd = res.data.password || loginForm.password;
        const loginRes = await axios.post(`${API}/api/auction-clients/login`, { email: loginForm.email, password: pwd });
        localStorage.setItem("auction_token", loginRes.data.token);
        localStorage.setItem("auction_client", JSON.stringify(loginRes.data.client));
        setClient(loginRes.data.client);
        setShowLogin(false);
        toast.success("Cont creat!");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare"); }
  };

  const handleVote = async () => {
    const p = parseFloat(price);
    if (!p) { toast.error("Introduceti un pret"); return; }
    try {
      const token = localStorage.getItem("auction_token");
      const res = await axios.post(`${API}/api/auctions/${data.auction.auction_id}/propose-price`, { price: p }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setPrice("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare"); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-pulse text-primary">Se incarca...</div></div>;
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Scuter negasit</p></div>;

  const photos = data.photos || [];
  const tf = data.technical_file || {};
  const acteLabel = tf.tip_acte === "carte_rar" ? "Acte Romania" : tf.tip_acte === "carte_strainatate" ? "Acte strainatate" : tf.tip_acte === "fara_acte" ? "Doar factura" : "-";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Fullscreen */}
      {fullscreen && photos[photoIndex] && (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" onClick={() => setFullscreen(false)}>
          <img src={photos[photoIndex]} alt="" className="max-w-full max-h-full object-contain" />
          <button onClick={() => setFullscreen(false)} className="absolute top-4 right-4 bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center"><X className="w-6 h-6" /></button>
          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setPhotoIndex(i => (i - 1 + photos.length) % photos.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={(e) => { e.stopPropagation(); setPhotoIndex(i => (i + 1) % photos.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
        </div>
      )}

      {/* Header */}
      <header className="bg-card border-b border-border/50 py-3 px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="font-bold text-primary">SCUTERSH</Link>
          <Badge className={data.status === "Vandut" ? "bg-gray-500" : data.status === "In licitatie" ? "bg-red-500 animate-pulse" : "bg-emerald-500"}>
            {data.status}
          </Badge>
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Photo */}
        {photos.length > 0 && (
          <div className="relative aspect-[4/3] bg-black rounded-xl overflow-hidden cursor-pointer" onClick={() => setFullscreen(true)}>
            <img src={photos[photoIndex]} alt="" className="w-full h-full object-contain" />
            {photos.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">{photoIndex + 1}/{photos.length}</div>
            )}
          </div>
        )}

        {/* Title + Price */}
        <div className="text-center">
          <h1 className="text-2xl font-bold">{data.brand} {data.model} {data.year}</h1>
          <p className="text-3xl font-bold text-primary mt-2">{data.selling_price} EUR</p>
          <p className="text-sm text-muted-foreground font-mono">{data.scooter_id}</p>
        </div>

        {/* Verification badge */}
        {data.verified ? (
          <div className="text-center">
            <Badge className="bg-emerald-500/20 text-emerald-400 px-3 py-1">
              <CheckCircle className="w-4 h-4 mr-1" /> Verificat mecanic — Nota: {data.avg_score}/5
            </Badge>
          </div>
        ) : (
          <div className="text-center">
            <Badge className="bg-yellow-500/20 text-yellow-400 px-3 py-1">
              <Info className="w-4 h-4 mr-1" /> Neverificat de noi
            </Badge>
          </div>
        )}

        {/* Technical specs */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" /> Fisa tehnica</h3>
            <div className="grid grid-cols-2 gap-1">
              {[
                { l: "Marca", v: data.brand },
                { l: "Model", v: data.model },
                { l: "An", v: data.year },
                { l: "Motor", v: data.engine_cc ? `${data.engine_cc} cc` : "-" },
                { l: "Km", v: data.mileage ? data.mileage.toLocaleString() : "-" },
                { l: "Culoare", v: data.color || "-" },
                { l: "VIN", v: data.vin || "-" },
                { l: "Combustibil", v: tf.fuel_type || "Benzina" },
                { l: "Transmisie", v: tf.transmission || "CVT" },
                { l: "Viteza max", v: tf.max_speed || "-" },
                { l: "Consum", v: tf.consumption || "-" },
                { l: "Acte", v: acteLabel },
              ].map(r => (
                <div key={r.l} className="flex justify-between text-sm py-1 border-b border-border/20">
                  <span className="text-muted-foreground">{r.l}</span>
                  <span className="font-semibold text-right">{r.v}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Auction section */}
        {data.auction && (
          <Card className="border-red-500/30">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-red-500 text-white animate-pulse"><Gavel className="w-3 h-3 mr-1" /> LICITATIE ACTIVA</Badge>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Pret pornire</p>
                <p className="text-lg">{data.auction.starting_price} EUR</p>
                <p className="text-sm text-muted-foreground mt-2">Cea mai mare oferta</p>
                <p className="text-3xl font-bold text-emerald-400">{data.auction.current_price} EUR</p>
                <p className="text-xs text-muted-foreground">{data.auction.bids_count} propuneri</p>
              </div>

              {client ? (
                <div className="flex gap-2">
                  <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder={`${data.auction.current_price + 10} EUR`} className="flex-1 text-lg font-bold" />
                  <Button onClick={handleVote} size="lg">Voteaza</Button>
                </div>
              ) : (
                <Button onClick={() => setShowLogin(true)} className="w-full" size="lg">
                  <UserPlus className="w-4 h-4 mr-2" /> Creati cont gratuit pentru a vota
                </Button>
              )}

              <Link to={`/auctions/${data.auction.auction_id}`}>
                <Button variant="outline" className="w-full mt-2">Vezi detalii complete</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground pt-4 pb-8">
          <p className="font-bold text-primary">SCUTERSH</p>
          <p>Sura Mare, Sector 4, Bucuresti</p>
          <p>+40792250058 | scutersh.store</p>
        </div>
      </div>

      {/* Login overlay */}
      {showLogin && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowLogin(false)}>
          <div className="bg-card rounded-xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold">{loginForm.mode === "login" ? "Conectare" : "Cont nou"}</h2>
            <div className="flex gap-2">
              <Button variant={loginForm.mode === "login" ? "default" : "outline"} onClick={() => setLoginForm(f => ({...f, mode: "login"}))} className="flex-1">Conectare</Button>
              <Button variant={loginForm.mode === "register" ? "default" : "outline"} onClick={() => setLoginForm(f => ({...f, mode: "register"}))} className="flex-1">Cont nou</Button>
            </div>
            <form onSubmit={handleLogin} className="space-y-3">
              {loginForm.mode === "register" && (
                <>
                  <Input placeholder="Nume complet *" value={loginForm.name} onChange={e => setLoginForm(f => ({...f, name: e.target.value}))} />
                  <Input placeholder="Telefon *" value={loginForm.phone} onChange={e => setLoginForm(f => ({...f, phone: e.target.value}))} />
                </>
              )}
              <Input type="email" placeholder="Email *" value={loginForm.email} onChange={e => setLoginForm(f => ({...f, email: e.target.value}))} />
              <Input type="password" placeholder="Parola *" value={loginForm.password} onChange={e => setLoginForm(f => ({...f, password: e.target.value}))} />
              <Button type="submit" className="w-full">{loginForm.mode === "login" ? "Conectare" : "Creati cont"}</Button>
            </form>
            <p className="text-xs text-muted-foreground text-center">Inscrierea este gratuita</p>
          </div>
        </div>
      )}
    </div>
  );
}
